import type { DocBlock, DocFormulaFieldBlock } from "@/lib/document-builder/blocks/blockTypes";
import { flattenDocBlocks } from "@/lib/document-builder/blocks/blockFlatten";
import { sortDocBlocks } from "@/lib/document-builder/blocks/blockReorder";
import type { DocVariableValues } from "@/lib/document-builder/variables/variableFill";
import { evaluateDocFormulaExpression } from "./docFormulaExpr";
import type { DocFormulaEvalResult } from "./docFormulaTypes";

export type DocFormulaEvaluation = {
  byBlockId: Map<string, DocFormulaEvalResult>;
  byKey: Map<string, DocFormulaEvalResult>;
};

/** Parse a manual fill value into a signed number, or null when unset/unparseable. */
export function parseDocFillNumber(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  return value;
}

function isFormulaBlock(block: DocBlock): block is DocFormulaFieldBlock {
  return block.type === "formula_field";
}

/**
 * Evaluate formula_field blocks in document sort order. Variables seed the
 * namespace; each successful formula adds its key for downstream expressions.
 * Mirrors formatCalcEval's ordered running-total discipline for document scope.
 */
export function evaluateDocFormulaFields(args: {
  blocks: readonly DocBlock[];
  variableValues: DocVariableValues;
}): DocFormulaEvaluation {
  const byBlockId = new Map<string, DocFormulaEvalResult>();
  const byKey = new Map<string, DocFormulaEvalResult>();
  const namespace: Record<string, number> = {};

  for (const [key, raw] of Object.entries(args.variableValues)) {
    const parsed = parseDocFillNumber(raw);
    if (parsed !== null) namespace[key] = parsed;
  }

  const formulaBlocks = sortDocBlocks(flattenDocBlocks(args.blocks)).filter(isFormulaBlock);

  for (const block of formulaBlocks) {
    const result = evaluateDocFormulaExpression(block.expression, namespace);
    byBlockId.set(block.id, result);
    byKey.set(block.key, result);
    if (result.ok) {
      namespace[block.key] = result.value;
    }
  }

  return { byBlockId, byKey };
}

export function getDocFormulaResult(
  evaluation: DocFormulaEvaluation | null | undefined,
  block: DocFormulaFieldBlock,
): DocFormulaEvalResult | undefined {
  return evaluation?.byBlockId.get(block.id) ?? evaluation?.byKey.get(block.key);
}
